// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react'
import {
  DesignVersionProvider,
  DialogUnwrapped,
  ButtonPrimary,
  ParticleDesignVersions
} from '@klarna/bubble-ui'
import { setupSentry, reportError } from '../../sentry'
import LearnMoreContent from './LearnMoreDefault/LearnMoreDefault'
import ErrorBoundary from '../ErrorBoundary'

import Bespoke from './bespoke'
import { getCopyParams, getLocale, getLanguageCode } from './utils'

import initTranslation from './utils/initTranslation'
import sendInterstitialEvent from '../../../tracking/sendInterstitialEvent/sendInterstitialEvent'
import { TokenParams } from './utils/createToken/createToken'
import styles from '../../widgets/css/index.css'
import backgroundImg from '../../widgets/css/background.jpg'
import GkdAuthParams from '../../../types/GkdAuthParams'

import NewInterstitials from '../../../newInterstitials'
import { startPrequalification } from '../../../utils/prequalification/startPrequalification'
import ProgrammaticPrequalLoader from './ProgrammaticPrequalLoader'
import { SkeletonLoader } from '../../../commons/skeletonLoader'
import { useMobileSDKContext } from '../../../services/MobileSDKMessaging/MobileSDKMessagingContext'
import { waitForVisibility } from '../../../utils/waitForVisibility/waitForVisibility'

interface LearnMoreComponentProps {
  params: TokenParams
  onClose?: () => void
  showBackground: boolean
  focusTrapActive?: boolean
  isWebSDK?: boolean
  clientKey?: string
  gkdAuthParams: GkdAuthParams
  sendMessage: (data: any, message: string) => void
}

export default function LearnMoreComponent({
  params,
  onClose,
  showBackground,
  focusTrapActive = true,
  isWebSDK = false,
  gkdAuthParams = {},
  sendMessage
}: LearnMoreComponentProps) {
  const legacyOSMClientId = params.legacyOSMClientId || params.clientId
  const { interstitialTriggerer } = params
  const transformedParams = {
    ...params, legacyOSMClientId, interstitialTriggerer, focusTrapActive, showButtons: onClose ?? false
  }

  if (params?.openPreQualFlow && params?.isProgrammatic) {
    // Opened by Klarna.Messaging.Prequalification.initiate() in the Web SDK
    return <ProgrammaticPrequalLoader params={params} gkdAuthParams={gkdAuthParams} sendMessage={sendMessage} />
  }

  if (params?.openPreQualFlow) {
    // Wait for the component to become visible before starting prequalification
    // This prevents unnecessary backend calls when the component is loaded but hidden
    waitForVisibility().then(() => {
      startPrequalification(transformedParams, params.language, params.country, transformedParams.showButtons)
    })
    return (
      <SkeletonLoader/>
    )
  }

  delete transformedParams.clientId // removing this key to clear confusion between GKD Auth clientId and OSM clientId

  const designVersion = transformedParams.theme === 'dark'
    ? ParticleDesignVersions.WAFFLE_DARK
    : ParticleDesignVersions.WAFFLE_LIGHT

  const rootElement = document.getElementById('root')
  if (rootElement) {
    rootElement.ariaModal = true
    rootElement.role = 'dialog'
    if (document.getElementById('payment-calculator-title')) {
      rootElement.setAttribute('aria-labelledby', 'payment-calculator-title')
    }
    rootElement.tabIndex = -1
  }

  if (document.documentElement) {
    document.documentElement.lang = getLanguageCode(transformedParams)
  }

  const { messagingSDK } = useMobileSDKContext()

  const handleCloseInterstitial = useCallback(async () => {
    // send signals to close fullscreen view if in mobile SDK context
    if (messagingSDK) {
      try {
        await messagingSDK.hideSeparateFullscreen()
      } catch (err) {
        // silent error (fallback mechanism)
      }
    }

    if (onClose) {
      onClose()
    }
  }, [onClose, messagingSDK])

  if (
    [
      'payment-information-generic-interstitial',
      'payment-calculator-interstitial',
      'payment-calculator-interstitial-revamped',
      'simple-timeline-based-interstitial',
      'deals-interstitial'
    ].includes(transformedParams.bespokeId) && !params.openPreQualFlow
  ) {
    const props = { ...transformedParams, ...gkdAuthParams }
    return (
      <NewInterstitials {...props} onClose={handleCloseInterstitial} role='dialog' sendMessage={sendMessage} />
    )
  }

  const [{ translator, copy, error }, setTranslation] = useState<any>({})

  setupSentry()

  useEffect(() => {
    sendInterstitialEvent(transformedParams.locale, 'interstitial_view', {
      ...transformedParams,
      type: 'learn-more',
      bespokeId:
        transformedParams.bespokeId && Bespoke[transformedParams.bespokeId] ? transformedParams.bespokeId : '',
      translation: getLocale(transformedParams),
      isWebSDK,
      k: params?.trackerParameters?.k,
    })

    initTranslation(transformedParams, gkdAuthParams).then((translation: any) => {
      setTranslation(translation)
    })
  }, [params]) // intentionally set to this instead of transformedParams, useEffect will render multiple times if we use transformedParams

  if (!translator) return null

  const footerButtons = []
  const adType = getCopyParams(transformedParams).copyParams[0]

  if (onClose) {
    const closeButton = {
      Component: ButtonPrimary,
      props: {
        children: translator.translate('buttons.close'),
        loading: false,
        id: 'learn-more-close-button',
        onClick: handleCloseInterstitial,
      },
    }
    footerButtons.push(closeButton)
  }

  if (error) {
    sendInterstitialEvent(transformedParams.locale, 'interstitial_error', {
      error,
      isWebSDK,
      ...params,
      k: params?.trackerParameters?.k,
    })
    reportError(
      {
        ...transformedParams,
        copy: copy ? JSON.stringify(copy) : null,
      },
      error
    )
  }

  let Component = () => <LearnMoreContent copy={copy} params={transformedParams} />

  const backgroundTheme = transformedParams.theme !== 'dark'
    && (transformedParams.bespokeId === 'external_content'
      || transformedParams.bespokeId === 'checkout')
    ? 'colors/backgrounds/subtle-inline'
    : 'colors/backgrounds/default'

  if (transformedParams.bespokeId && Bespoke[transformedParams.bespokeId]) {
    Component = Bespoke[transformedParams.bespokeId]
  }

  const onError = (err: any) => {
    sendInterstitialEvent(transformedParams.locale, 'interstitial_error', {
      error: err,
      isWebSDK,
      ...params,
      k: params?.trackerParameters?.k,
    })
    reportError(
      {
        ...transformedParams,
        copy: copy ? JSON.stringify(copy) : null,
      },
      err
    )
  }

  return (

    <div
      role='dialog'
      aria-labelledby='payment-calculator-title'
      id={showBackground ? 'widget-with-background' : undefined}
      style={
        showBackground
          ? {
            backgroundImage: `url(${backgroundImg})`,
          }
          : null
      }
      className={isWebSDK ? 'websdk' : undefined}
    >
      <style>{styles}</style>
      <ErrorBoundary
        onClose={handleCloseInterstitial}
        translator={translator}
        onError={(err: any) => onError(err)}
      >
        <DesignVersionProvider value={designVersion}>
          <DialogUnwrapped
            focusTrapActive={focusTrapActive}
            onClose={onClose ? handleCloseInterstitial : null}
            captureEscapeKey={true}
            rightIconAriaLabel={translator.translate('buttons.close')}
            onBackgroundClick={handleCloseInterstitial}
            klarnaLogo
            klarnaLogoAccessibilityLabel="Klarna logo"
            footer={footerButtons}
            backgroundColorToken={backgroundTheme}
            id={`learn-more-dialog-${transformedParams.bespokeId || 'default'}-${
              transformedParams.theme || 'default'
            }`}
            isFullScreen={true}
          >
            <div id="interstitial-wrapper">
              <Component
                designVersion={designVersion}
                copy={copy}
                closeButtonLabel={translator.translate('buttons.close')}
                buttons={footerButtons}
                onClose={handleCloseInterstitial}
                theme={transformedParams.theme}
                adType={adType}
              />
            </div>
          </DialogUnwrapped>
        </DesignVersionProvider>
      </ErrorBoundary>
    </div>
  )
}
